/**
 * MUI Theme V2 — Horizon Travel Agency
 */

import { createTheme } from '@mui/material/styles';
import { colors } from './colors';
import { muiTypography } from './typography';

export const themeV2 = createTheme({
  palette: {
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
    },
    success: {
      main: colors.success.main,
      light: colors.success.light,
      dark: colors.success.dark,
    },
    warning: {
      main: colors.warning.main,
      light: colors.warning.light,
      dark: colors.warning.dark,
    },
    error: {
      main: colors.error.main,
      light: colors.error.light,
      dark: colors.error.dark,
    },
    info: {
      main: colors.info.main,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    divider: colors.border.light,
  },

  typography: muiTypography,
  spacing: 8,

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  shape: {
    borderRadius: 10,
  },

  shadows: [
    'none',
    '0 1px 2px rgba(14, 28, 42, 0.06)',
    '0 2px 8px rgba(14, 28, 42, 0.06)',
    '0 4px 12px rgba(14, 28, 42, 0.07)',
    '0 8px 20px rgba(14, 28, 42, 0.08)',
    '0 12px 28px rgba(14, 28, 42, 0.09)',
    '0 16px 36px rgba(14, 28, 42, 0.1)',
    '0 20px 40px rgba(14, 28, 42, 0.1)',
    '0 24px 48px rgba(14, 28, 42, 0.12)',
    '0 28px 56px rgba(14, 28, 42, 0.12)',
    ...Array(15).fill('0 28px 56px rgba(14, 28, 42, 0.12)'),
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          color: colors.text.primary,
        },
        '::selection': {
          backgroundColor: 'rgba(31, 111, 120, 0.25)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          padding: '10px 22px',
          boxShadow: 'none',
          transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: colors.primary.main,
          '&:hover': {
            backgroundColor: colors.primary.light,
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          color: '#fff',
          '&:hover': {
            backgroundColor: colors.secondary.dark,
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: 'none',
          border: `1px solid ${colors.border.light}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: colors.neutral.white,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },

  custom: {
    brandAccent: colors.accent.main,
    seaTeal: colors.secondary.main,
    borderRadius: {
      sm: 6,
      md: 10,
      lg: 14,
      xl: 18,
      '2xl': 24,
    },
    boxShadow: {
      sm: '0 1px 2px rgba(14, 28, 42, 0.05)',
      md: '0 4px 14px rgba(14, 28, 42, 0.07)',
      lg: '0 10px 28px rgba(14, 28, 42, 0.09)',
      xl: '0 18px 40px rgba(14, 28, 42, 0.1)',
      '2xl': '0 24px 48px rgba(14, 28, 42, 0.12)',
    },
  },
});

export default themeV2;
